/**
 * 字牌（跑胡子）核心游戏引擎
 */
import {
  Card,
  Player,
  GameState,
  GameConfig,
  Meld,
  ChiOption,
  WinResult,
  BotDifficulty
} from './types';
import { Rules } from './Rules';
import { resolveRuleConfig, RuleConfig } from './rules.config';

export class GameEngine {
  private players: Player[] = [];
  private deck: Card[] = [];
  private currentState: GameState = GameState.IDLE;
  private currentTurnIndex: number = 0; // 当前出牌/摸牌玩家索引
  private dealerIndex: number = 0; // 庄家索引
  private publicCard: Card | null = null; // 判定区/公共区的牌 (摸出的牌或打出的牌)
  private isPublicCardDrawn: boolean = false; // 公共区的牌是摸出来的还是别人打出来的
  private currentRound: number = 1;
  private winResult: WinResult | null = null;
  private config: GameConfig;

  constructor(config: GameConfig) {
    this.config = config;
    this.initializePlayers();
    this.resetMatch();
  }

  public updateConfig(config: GameConfig): void {
    this.config = config;
  }

  private getRuleConfig(): RuleConfig {
    return resolveRuleConfig({ ...this.config.rules, minHuxiToWin: this.config.minHuxiToWin });
  }

  /**
   * 初始化三位玩家（南家-真人，东家-AI老手，西家-AI老手）
   */
  private initializePlayers(): void {
    this.players = [
      {
        id: 'player_0',
        name: '我 (玩家)',
        avatar: '🀄',
        position: 'bottom',
        isDealer: true,
        hand: [],
        handColumns: [],
        melds: [],
        discards: [],
        score: 0,
        roundHuxi: 0,
        isBot: false
      },
      {
        id: 'player_1',
        name: '王麻子 (老司机)',
        avatar: '🧔',
        position: 'right',
        isDealer: false,
        hand: [],
        handColumns: [],
        melds: [],
        discards: [],
        score: 0,
        roundHuxi: 0,
        isBot: true,
        botDifficulty: this.config.botDifficulty || BotDifficulty.MEDIUM
      },
      {
        id: 'player_2',
        name: '李老八 (雀圣)',
        avatar: '👴',
        position: 'left',
        isDealer: false,
        hand: [],
        handColumns: [],
        melds: [],
        discards: [],
        score: 0,
        roundHuxi: 0,
        isBot: true,
        botDifficulty: this.config.botDifficulty || BotDifficulty.HARD
      }
    ];
  }

  /**
   * 重置整场比赛
   */
  public resetMatch(): void {
    this.currentRound = 1;
    this.dealerIndex = 0;
    this.players.forEach(p => {
      p.score = 0;
    });
    this.resetRound();
  }

  /**
   * 重置并开始新一局
   */
  public resetRound(): void {
    this.publicCard = null;
    this.isPublicCardDrawn = false;
    this.winResult = null;
    this.currentState = GameState.IDLE;

    // 清理玩家每局数据
    this.players.forEach((p, idx) => {
      p.isDealer = idx === this.dealerIndex;
      p.hand = [];
      p.handColumns = [];
      p.melds = [];
      p.discards = [];
      p.roundHuxi = 0;
    });

    // 重新洗牌
    this.deck = Rules.shuffleDeck(Rules.createDeck(this.getRuleConfig()));
    this.currentTurnIndex = this.dealerIndex;
  }

  /**
   * 开始新一局发牌
   */
  public startNewRound(): void {
    this.resetRound();
    this.currentState = GameState.DEALING;
    this.dealCards();
  }

  /**
  * 发牌：每位玩家先发总手牌数减一张，庄家再补一张。
   */
  private dealCards(): void {
    const baseHandCount = (this.config.handSize || 21) - 1;

    // 先给每位玩家发基础手牌数
    for (let i = 0; i < this.players.length; i++) {
      const pIdx = (this.dealerIndex + i) % this.players.length;
      const handCards: Card[] = [];

      for (let c = 0; c < baseHandCount; c++) {
        if (this.deck.length > 0) {
          handCards.push(this.deck.pop()!);
        }
      }

      this.players[pIdx].hand = handCards;
      this.players[pIdx].handColumns = Rules.organizeHandColumns(handCards);
      this.updatePlayerHuxi(this.players[pIdx]);
    }

    // 庄家再进一张，形成庄家起手21张。
    const dealer = this.getDealer();
    if (this.deck.length > 0) {
      dealer.hand.push(this.deck.pop()!);
      dealer.handColumns = Rules.organizeHandColumns(dealer.hand);
      this.updatePlayerHuxi(dealer);
    }

    // 所有玩家起手有四张同牌时立即亮出，不补牌也不出牌。
    this.players.forEach(player => {
      let tiCard = Rules.canTi(player.hand);
      while (tiCard) {
        this.executeInitialTi(player.id, tiCard);
        tiCard = Rules.canTi(player.hand);
      }
    });

    // 单提仍需庄家出一张，多提则不出牌，直接轮到下一家摸牌。
    this.currentTurnIndex = this.dealerIndex;
    const dealerInitialTiCount = dealer.melds.filter(meld => meld.id.startsWith('ti_init_')).length;
    if (dealerInitialTiCount > 1) {
      this.currentTurnIndex = (this.dealerIndex + 1) % this.players.length;
      this.currentState = GameState.WAITING_DRAW;
    } else {
      this.currentState = GameState.WAITING_DISCARD;
    }
  }

  /**
   * 庄家起手自动提牌
   */
  private executeInitialTi(playerId: string, card: Card): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    const matching = player.hand.filter(c => Rules.isSameCard(c, card));
    if (matching.length === 4) {
      player.hand = player.hand.filter(c => !Rules.isSameCard(c, card));
      const meld: Meld = {
        id: `ti_init_${card.id}`,
        type: 'TI',
        cards: matching,
        huxi: Rules.getMeldHuxi('TI', card.isBig, this.getRuleConfig()),
        isExposed: true
      };
      player.melds.push(meld);

      player.handColumns = Rules.organizeHandColumns(player.hand);
      this.updatePlayerHuxi(player);
    }
  }

  /**
   * 当前回合玩家摸牌进入判定区
   */
  public drawCard(playerId: string): Card | null {
    if (this.deck.length === 0) {
      // 牌抓光了 -> 荒庄 (流局)
      this.handleDrawGame();
      return null;
    }

    const card = this.deck.pop()!;
    this.publicCard = card;
    this.isPublicCardDrawn = true;
    this.currentState = GameState.CARD_DRAWN;

    return card;
  }

  /**
   * 玩家打出一张手牌进入判定区
   */
  public discardCard(playerId: string, cardId: string): Card | null {
    const player = this.getPlayer(playerId);
    if (!player) return null;

    const cIdx = player.hand.findIndex(c => c.id === cardId);
    if (cIdx === -1) return null;

    const [discarded] = player.hand.splice(cIdx, 1);
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    this.publicCard = discarded;
    this.isPublicCardDrawn = false;
    this.currentState = GameState.CARD_DRAWN;

    return discarded;
  }

  /**
   * 执行“提”
   */
  public executeTi(playerId: string, card: Card): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    const existingSao = player.melds.find(
      m => m.type === 'SAO' && Rules.isSameCard(m.cards[0], card)
    );
    if (existingSao) {
      existingSao.type = 'TI';
      existingSao.cards.push(card);
      existingSao.huxi = Rules.getMeldHuxi('TI', card.isBig, this.getRuleConfig());
    } else {
    const matching = player.hand.filter(c => Rules.isSameCard(c, card));
    player.hand = player.hand.filter(c => !Rules.isSameCard(c, card));

    const meld: Meld = {
      id: `ti_${card.id}`,
      type: 'TI',
      cards: [...matching, card],
      huxi: Rules.getMeldHuxi('TI', card.isBig, this.getRuleConfig()),
      isExposed: true
    };
    player.melds.push(meld);
    }

    // 提牌后从牌堆摸一张补牌
    if (this.deck.length > 0) {
      player.hand.push(this.deck.pop()!);
    }

    this.publicCard = null;
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    // 提牌后该玩家需要打出一张牌 (若没有直接胡牌)
    this.currentTurnIndex = this.players.findIndex(p => p.id === playerId);
    this.currentState = GameState.WAITING_DISCARD;
  }

  /**
   * 执行“扫”
   */
  public executeSao(playerId: string, card: Card): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    const matching = player.hand.filter(c => Rules.isSameCard(c, card));
    player.hand = player.hand.filter(c => !Rules.isSameCard(c, card));

    const meld: Meld = {
      id: `sao_${card.id}`,
      type: 'SAO',
      cards: [...matching, card],
      huxi: Rules.getMeldHuxi('SAO', card.isBig, this.getRuleConfig()),
      isExposed: true
    };
    player.melds.push(meld);

    this.publicCard = null;
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    this.currentTurnIndex = this.players.findIndex(p => p.id === playerId);
    this.currentState = GameState.WAITING_DISCARD;
  }

  /**
   * 执行“跑”
   */
  public executePao(playerId: string, card: Card): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    // 1. 如果已有碰或扫，升级该面子为跑
    const existingMeld = player.melds.find(
      m => (m.type === 'PENG' || m.type === 'SAO') && Rules.isSameCard(m.cards[0], card)
    );

    if (existingMeld) {
      existingMeld.type = 'PAO';
      existingMeld.cards.push(card);
      existingMeld.huxi = Rules.getMeldHuxi('PAO', card.isBig, this.getRuleConfig());
    } else {
      // 2. 如果是手牌中的坎被跑
      const matching = player.hand.filter(c => Rules.isSameCard(c, card));
      player.hand = player.hand.filter(c => !Rules.isSameCard(c, card));
      player.melds.push({
        id: `pao_${card.id}`,
        type: 'PAO',
        cards: [...matching, card],
        huxi: Rules.getMeldHuxi('PAO', card.isBig, this.getRuleConfig()),
        isExposed: true
      });
    }

    this.publicCard = null;
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    // 跑牌后只允许当前玩家立即出一张牌；出牌流程结束后再轮到下一家摸牌。
    this.currentTurnIndex = this.players.findIndex(p => p.id === playerId);
    this.currentState = GameState.WAITING_DISCARD;
  }

  /**
   * 执行“碰”
   */
  public executePeng(playerId: string, card: Card): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    // 从手牌中移除2张相同牌
    let removed = 0;
    const pengCards: Card[] = [];
    player.hand = player.hand.filter(c => {
      if (Rules.isSameCard(c, card) && removed < 2) {
        removed++;
        pengCards.push(c);
        return false;
      }
      return true;
    });

    player.melds.push({
      id: `peng_${card.id}`,
      type: 'PENG',
      cards: [...pengCards, card],
      huxi: Rules.getMeldHuxi('PENG', card.isBig, this.getRuleConfig()),
      isExposed: true
    });

    this.publicCard = null;
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    // 碰牌后，轮到该玩家出牌
    this.currentTurnIndex = this.players.findIndex(p => p.id === playerId);
    this.currentState = GameState.WAITING_DISCARD;
  }

  /**
   * 执行“吃”
   */
  public executeChi(playerId: string, option: ChiOption): void {
    const player = this.getPlayer(playerId);
    if (!player) return;

    const biCards = option.biCards || [];
    const handCardIds = new Set([
      ...option.handCards.map(c => c.id),
      ...biCards.flatMap(bi => bi.handCards.map(c => c.id)),
      ...biCards.flatMap(bi => bi.meldCards.filter(c => c.id !== option.targetCard.id).map(c => c.id))
    ]);
    player.hand = player.hand.filter(c => !handCardIds.has(c.id));

    player.melds.push({
      id: `chi_${option.id}_${Math.random()}`,
      type: option.type,
      cards: option.meldCards,
      huxi: option.huxi,
      isExposed: true
    });
    biCards.forEach((bi, index) => {
      player.melds.push({
        id: `bi_${option.id}_${index}`,
        type: bi.type,
        cards: bi.meldCards,
        huxi: bi.huxi,
        isExposed: true
      });
    });

    this.publicCard = null;
    player.handColumns = Rules.organizeHandColumns(player.hand);
    this.updatePlayerHuxi(player);

    // 吃牌后，轮到该玩家出牌
    this.currentTurnIndex = this.players.findIndex(p => p.id === playerId);
    this.currentState = GameState.WAITING_DISCARD;
  }

  /**
   * 判定区牌无人要 -> 变为臭牌进入弃牌池
   */
  public passPublicCard(): void {
    if (!this.publicCard) return;

    const activePlayer = this.getCurrentPlayer();
    if (activePlayer) {
      activePlayer.discards.push(this.publicCard);
    }

    this.publicCard = null;

    // 轮到下一位玩家摸牌/出牌
    this.nextTurn();
  }

  /**
   * 轮到下一位玩家摸牌
   */
  public nextTurn(): void {
    this.currentTurnIndex = (this.currentTurnIndex + 1) % this.players.length;
    this.currentState = GameState.WAITING_DRAW;
  }

  /**
   * 处理胡牌胜利结算
   */
  public handleWin(winnerId: string, winResult: WinResult): void {
    this.winResult = winResult;
    this.winResult.winnerId = winnerId;
    this.currentState = GameState.ROUND_OVER;

    const winner = this.getPlayer(winnerId);
    if (!winner) return;

    // 计分逻辑 (底分10 + 胡息数)
    const baseWinScore = winResult.totalHuxi;
    const loserCount = this.players.length - 1;

    this.players.forEach(p => {
      if (p.id === winnerId) {
        const gain = baseWinScore * loserCount;
        p.score += gain;
        winResult.scoreDelta[p.id] = gain;
      } else {
        const loss = -baseWinScore;
        p.score += loss;
        winResult.scoreDelta[p.id] = loss;
      }
    });

    // 赢家下一局坐庄，否则流庄
    const winnerIndex = this.players.findIndex(p => p.id === winnerId);
    this.dealerIndex = winnerIndex;
  }

  /**
   * 荒庄 / 流局处理
   */
  private handleDrawGame(): void {
    this.currentState = GameState.ROUND_OVER;
    this.winResult = {
      isWin: false,
      winnerId: '',
      winCard: this.publicCard || {
        id: 'none',
        type: 'small',
        value: 1,
        isBig: false,
        isRed: false,
        name: '无',
        pinyin: ''
      },
      isSelfDraw: false,
      totalHuxi: 0,
      melds: [],
      eyes: [],
      specialPatterns: ['荒庄 (流局)'],
      scoreDelta: {}
    };

    // 荒庄时庄家下庄，轮给下一位
    this.dealerIndex = (this.dealerIndex + 1) % this.players.length;
  }

  /**
   * 实时更新玩家的胡息 (已亮牌 + 手牌暗坎/暗提)
   */
  public updatePlayerHuxi(player: Player): void {
    let total = 0;
    player.melds.forEach(m => (total += m.huxi));

    // 完整的四张同牌按暗提计分，三张同牌按暗坎计分。
    const counts = new Map<string, number>();
    player.hand.forEach(c => {
      const key = `${c.type}_${c.value}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    for (const [key, count] of counts.entries()) {
      if (count >= 4) {
        const isBig = key.startsWith('big');
        total += Rules.getMeldHuxi('TI', isBig, this.getRuleConfig());
      } else if (count === 3) {
        const isBig = key.startsWith('big');
        total += Rules.getMeldHuxi('KAN', isBig, this.getRuleConfig());
      }
    }

    player.roundHuxi = total;
  }

  // 辅助获取方法
  public getGameState(): GameState {
    return this.currentState;
  }

  public setGameState(state: GameState): void {
    this.currentState = state;
  }

  public getPlayers(): Player[] {
    return this.players;
  }

  public getPlayer(id: string): Player | undefined {
    return this.players.find(p => p.id === id);
  }

  public getCurrentPlayer(): Player {
    return this.players[this.currentTurnIndex];
  }

  public getCurrentTurnIndex(): number {
    return this.currentTurnIndex;
  }

  public getDealer(): Player {
    return this.players[this.dealerIndex];
  }

  public getDealerIndex(): number {
    return this.dealerIndex;
  }

  public getPublicCard(): Card | null {
    return this.publicCard;
  }

  public isPublicDrawn(): boolean {
    return this.isPublicCardDrawn;
  }

  public getDeckSize(): number {
    return this.deck.length;
  }

  public getWinResult(): WinResult | null {
    return this.winResult;
  }

  public getCurrentRound(): number {
    return this.currentRound;
  }

  public getTotalRounds(): number {
    return this.config.totalRounds || 4;
  }
}

